// Pure game / tournament logic — no React, no Firebase

export const WIN_BY = 2, MAX_PTS = 21, DEUCE_CAP = 30;

// Returns "A", "B", or null (game not yet over). 29–29: next point wins (30–29).
export const gw = (a, b) => {
  if (a === DEUCE_CAP && b === DEUCE_CAP - 1) return "A";
  if (b === DEUCE_CAP && a === DEUCE_CAP - 1) return "B";
  if (a >= MAX_PTS && a - b >= WIN_BY) return "A";
  if (b >= MAX_PTS && b - a >= WIN_BY) return "B";
  return null;
};

// Returns "A", "B", or null (match not yet over)
export const mw = (sets, n) =>
  sets.filter(s => s.winner === "A").length >= n ? "A"
  : sets.filter(s => s.winner === "B").length >= n ? "B"
  : null;

/** Server's current rally score (for service-court left/right). */
export const serverScore = (serving, scoreA, scoreB) =>
  serving === "A" ? scoreA : scoreB;

/**
 * Service-court marker for a side (server or receiver) from server score parity.
 * Portrait viewBox 200×439; landscape 439×200.
 */
export function serviceCourtCoords(portrait, side, endsSwapped, evenScore) {
  if (portrait) {
    const py =
      side === "A"
        ? endsSwapped
          ? 360
          : 79
        : endsSwapped
          ? 79
          : 360;
    const topHalf = py < 219.5;
    const px = topHalf
      ? evenScore
        ? 60
        : 140
      : evenScore
        ? 140
        : 60;
    return { x: px, y: py };
  }
  const px =
    side === "A"
      ? endsSwapped
        ? 360
        : 79
      : endsSwapped
        ? 79
        : 360;
  const leftHalf = px < 219.5;
  const py = leftHalf
    ? evenScore
      ? 140
      : 60
    : evenScore
      ? 60
      : 140;
  return { x: px, y: py };
}

/** Portrait marker (viewBox 200×439). Even score → server's right court while facing the net. */
export const serveMarkerPortrait = (serving, scoreA, scoreB, endsSwapped = false) => {
  const even = serverScore(serving, scoreA, scoreB) % 2 === 0;
  return serviceCourtCoords(true, serving, endsSwapped, even);
};

/** Landscape marker (viewBox 439×200). Even score → server's right court while facing the net. */
export const serveMarkerLandscape = (serving, scoreA, scoreB, endsSwapped = false) => {
  const even = serverScore(serving, scoreA, scoreB) % 2 === 0;
  return serviceCourtCoords(false, serving, endsSwapped, even);
};

/** After a rally, the scoring side serves next (badminton). */
export const servingAfterPoint = (scoringSide) => scoringSide;

export const shuffle = a => {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
};

export const mkMatch = (id, a, b, matchNo = null) => ({
  id: `m${id}`, matchNo, teamA: a, teamB: b,
  scoreA: null, scoreB: null, winner: null, status: "pending", isBye: false, setsDetail: ""
});

export const mkBye = (id, team, matchNo = null) => ({
  id: `b${id}`, matchNo, teamA: team, teamB: null,
  scoreA: null, scoreB: null, winner: team, status: "bye", isBye: true, setsDetail: ""
});

/** Standard bracket seed positions for size (power of 2). */
export function seedOrder(bracketSize) {
  let seeds = [1, 2];
  while (seeds.length < bracketSize) {
    const n = seeds.length * 2;
    seeds = [...seeds, ...seeds.map(s => n + 1 - s)];
  }
  return seeds;
}

export function knockoutBracketSize(teamCount) {
  return Math.pow(2, Math.ceil(Math.log2(Math.max(teamCount, 2))));
}

export function knockoutByeCount(teamCount) {
  return knockoutBracketSize(teamCount) - teamCount;
}

const roundNameForSlotCount = n =>
  n === 1 ? "🏆 Final"
  : n === 2 ? "Semi-Finals"
  : n === 4 ? "Quarter-Finals"
  : `Round of ${n * 2}`;

/**
 * Single-elimination bracket with standard seeding and minimal byes.
 * Vacant bracket holes (double-bye) collapse in later rounds so a lone
 * bye winner (e.g. 5-team → Red) sits in the Final, not a semi vs TBD.
 * @param {string[]} teams
 * @param {{ order?: string[] }} [options] — explicit seed order (same teams); defaults to shuffled
 */
export function buildKnockout(teams, options = {}) {
  // Duplicate labels break single-elim (same team can land in a playable QF
  // and as the auto-advanced bye into the following round).
  const uniqueTeams = [];
  const seen = new Set();
  for (const t of teams || []) {
    if (t == null || t === "") continue;
    if (seen.has(t)) continue;
    seen.add(t);
    uniqueTeams.push(t);
  }
  const teamCount = uniqueTeams.length;
  const bracketSize = knockoutBracketSize(teamCount);
  const byeCount = bracketSize - teamCount;
  const orderOpt =
    options.order && Array.isArray(options.order)
      ? [...new Set(options.order.filter((t) => t != null && t !== ""))].filter((t) =>
          seen.has(t)
        )
      : null;
  const effectiveOrder =
    orderOpt && orderOpt.length === teamCount
      ? orderOpt
      : shuffle([...uniqueTeams]);
  const seeds = seedOrder(bracketSize);

  /** Empty bracket hole — no team and no feeder match. */
  const isVacant = (s) => Boolean(s && s.isBye && !s.name && !s.fromMatch);

  let matchCounter = 1;
  let slots = seeds.map(seedNum => {
    const name = seedNum <= teamCount ? effectiveOrder[seedNum - 1] : null;
    return { name, isBye: name === null, orderIndex: seedNum <= teamCount ? seedNum - 1 : -1 };
  });

  const rounds = [];
  let roundIdx = 0;

  while (slots.length > 1) {
    const isFirst = roundIdx === 0;
    const matches = [];
    const nextSlots = [];
    for (let i = 0; i < slots.length; i += 2) {
      const slotA = slots[i];
      const slotB = slots[i + 1];
      const bracketIndex = i / 2;

      // Double vacancy — keep a hole so later pairing / layout stay aligned
      if (isVacant(slotA) && isVacant(slotB)) {
        nextSlots.push({ name: null, isBye: true, orderIndex: -1, fromMatch: null });
        continue;
      }

      // Later rounds: vacancy + one occupant → skip this round, advance occupant
      // (5-team: Red's QF bye feeds Final, not a semi vs TBD)
      if (!isFirst && (isVacant(slotA) || isVacant(slotB))) {
        const adv = isVacant(slotA) ? slotB : slotA;
        nextSlots.push({
          name: adv?.name || null,
          isBye: false,
          orderIndex: adv?.orderIndex ?? -1,
          fromMatch: adv?.fromMatch || null,
        });
        continue;
      }

      const matchNo = matchCounter++;
      const firstRoundBye =
        isFirst && (isVacant(slotA) || isVacant(slotB) || slotA.isBye || slotB?.isBye);
      const byeWinner = firstRoundBye
        ? isVacant(slotA) || slotA.isBye
          ? slotB
          : slotA
        : null;

      if (isFirst && (isVacant(slotA) || isVacant(slotB) || slotA.isBye || slotB?.isBye)) {
        // One real team vs empty seed — show QF bye card, then advance
        const adv = byeWinner;
        const advName = adv?.name || null;
        const bm = mkBye(matchNo, advName, matchNo);
        bm.bracketIndex = bracketIndex;
        if (adv?.orderIndex >= 0) bm.seedSlotA = adv.orderIndex;
        matches.push(bm);
        nextSlots.push({
          name: advName,
          isBye: false,
          orderIndex: -1,
          fromMatch: matchNo,
        });
        continue;
      }

      const teamA = slotA.name || "TBD";
      const teamB = slotB?.name || "TBD";

      const m = mkMatch(matchNo, teamA, teamB, matchNo);
      m.bracketIndex = bracketIndex;
      if (slotA.fromMatch) m.feedA = slotA.fromMatch;
      if (slotB?.fromMatch) m.feedB = slotB.fromMatch;
      if (isFirst) {
        if (slotA.orderIndex >= 0) m.seedSlotA = slotA.orderIndex;
        if (slotB?.orderIndex >= 0) m.seedSlotB = slotB.orderIndex;
      }
      matches.push(m);
      nextSlots.push({
        name: null, isBye: false, orderIndex: -1, fromMatch: matchNo,
      });
    }
    // Skip empty round shells (all slots collapsed) — still keep named rounds with matches
    if (matches.length) {
      rounds.push({ name: roundNameForSlotCount(slots.length / 2), matches });
    }
    slots = nextSlots;
    roundIdx++;
  }

  return {
    type: "knockout",
    rounds: propagateKnockout(rounds),
    teamOrder: effectiveOrder,
    bracketSize,
    byeCount,
  };
}

/** Rebuild knockout from seed order while keeping results for unchanged first-round pairings. */
export function rebuildKnockoutWithOrder(tourn, newOrder) {
  const fresh = buildKnockout(newOrder, { order: newOrder });
  const oldR0 = tourn.rounds?.[0]?.matches || [];
  fresh.rounds[0].matches = fresh.rounds[0].matches.map((m, i) => {
    const old = oldR0[i];
    if (!old || old.status === "pending") return m;
    if (old.teamA === m.teamA && old.teamB === m.teamB) {
      return { ...m, ...old, matchNo: m.matchNo, bracketIndex: m.bracketIndex };
    }
    return m;
  });
  fresh.rounds = propagateKnockout(fresh.rounds);
  return { ...tourn, ...fresh, teamOrder: newOrder };
}

export function buildRoundRobin(teams) {
  const t = [...teams]; if (t.length % 2) t.push(null);
  const rounds = [];
  let matchCounter = 1;
  for (let r = 0; r < t.length - 1; r++) {
    const matches = [];
    for (let i = 0; i < t.length / 2; i++) {
      const a = t[i], b = t[t.length - 1 - i];
      // Odd pools: one team sits out the round — do not emit bye fixture cards.
      if (a && b) matches.push(mkMatch(r * 100 + i, a, b, matchCounter++));
    }
    rounds.push({ name: `Round ${r + 1}`, matches });
    t.splice(1, 0, t.pop());
  }
  return { type: "round-robin", rounds };
}

export function buildQualifier(teams, topN, numGroups = 2) {
  const gc = Math.max(2, Math.min(numGroups, teams.length));
  const buckets = Array.from({ length: gc }, () => []);
  teams.forEach((name, i) => buckets[i % gc].push(name));
  const groupStages = buckets.filter(b => b.length > 0).map((g, gi) => {
    const rr = buildRoundRobin(g);
    return { groupName: `Group ${String.fromCharCode(65 + gi)}`, rounds: rr.rounds };
  });
  const knockoutTeams = Array.from({ length: topN }, (_, i) => `Q${i + 1}`);
  const ko = buildKnockout(knockoutTeams);
  return { type: "qualifier", groups: groupStages, knockout: ko, topN, numGroups: gc, groupsDone: false };
}

export function propagateKnockout(rounds) {
  const r = rounds.map((rnd) => ({
    ...rnd,
    matches: rnd.matches.map((m) => ({ ...m })),
  }));
  for (let ri = 0; ri < r.length - 1; ri++) {
    const cur = r[ri].matches;
    const next = r[ri + 1].matches;
    const nextByBi = new Map(
      next.map((m, i) => [
        typeof m.bracketIndex === "number" ? m.bracketIndex : i,
        m,
      ])
    );
    const prevTeams = next.map((m) => ({ a: m.teamA, b: m.teamB }));

    cur.forEach((m, i) => {
      const bi = typeof m.bracketIndex === "number" ? m.bracketIndex : i;
      const slot = nextByBi.get(Math.floor(bi / 2));
      if (!slot) return;
      const winner = m.winner || "TBD";
      if (bi % 2 === 0) slot.teamA = winner;
      else slot.teamB = winner;
    });

    next.forEach((slot, i) => {
      const prev = prevTeams[i];
      if (!prev) return;
      if (slot.teamA !== prev.a || slot.teamB !== prev.b) {
        slot.winner = null;
        slot.status = "pending";
        slot.scoreA = null;
        slot.scoreB = null;
        slot.setsDetail = "";
      }
    });
  }
  return r;
}

/** Sum rally points per side from setsDetail ("21-15, 21-18") or from score fields when they hold rally scores. */
export function matchRallyTotals(m) {
  const detail = m.setsDetail;
  if (detail && String(detail).trim()) {
    let ptsA = 0, ptsB = 0, n = 0;
    for (const part of String(detail).split(",")) {
      const hit = part.trim().match(/^(\d+)\s*-\s*(\d+)$/);
      if (!hit) continue;
      const sa = parseInt(hit[1], 10), sb = parseInt(hit[2], 10);
      if (isNaN(sa) || isNaN(sb)) continue;
      ptsA += sa;
      ptsB += sb;
      n++;
    }
    if (n) return { ptsA, ptsB };
  }
  const a = parseInt(m.scoreA, 10), b = parseInt(m.scoreB, 10);
  if (isNaN(a) || isNaN(b)) return null;
  // Fixture inputs often store a single-set rally score (e.g. 21–15); set wins are usually 0–3.
  if (a >= 15 || b >= 15 || a + b > 6) return { ptsA: a, ptsB: b };
  return null;
}

export function rrStandingsCalc(matches, teamNames) {
  const t = {}; teamNames.forEach(n => { t[n] = { w: 0, l: 0, d: 0, pts: 0, f: 0, a: 0, pd: 0 }; });
  matches.forEach(m => {
    if (m.status !== "done" || m.isBye) return;

    const rally = matchRallyTotals(m);
    if (rally) {
      if (t[m.teamA]) { t[m.teamA].f += rally.ptsA; t[m.teamA].a += rally.ptsB; t[m.teamA].pd += rally.ptsA - rally.ptsB; }
      if (t[m.teamB]) { t[m.teamB].f += rally.ptsB; t[m.teamB].a += rally.ptsA; t[m.teamB].pd += rally.ptsB - rally.ptsA; }
    } else {
      const a = parseInt(m.scoreA, 10), b = parseInt(m.scoreB, 10);
      if (!isNaN(a) && !isNaN(b)) {
        if (t[m.teamA]) { t[m.teamA].f += a; t[m.teamA].a += b; t[m.teamA].pd += a - b; }
        if (t[m.teamB]) { t[m.teamB].f += b; t[m.teamB].a += a; t[m.teamB].pd += b - a; }
      }
    }

    if (m.winner === m.teamA) {
      if (t[m.teamA]) { t[m.teamA].w++; t[m.teamA].pts += 3; }
      if (t[m.teamB]) t[m.teamB].l++;
    } else if (m.winner === m.teamB) {
      if (t[m.teamB]) { t[m.teamB].w++; t[m.teamB].pts += 3; }
      if (t[m.teamA]) t[m.teamA].l++;
    } else {
      const a = parseInt(m.scoreA, 10), b = parseInt(m.scoreB, 10);
      if (isNaN(a) || isNaN(b)) return;
      if (a > b) { if (t[m.teamA]) { t[m.teamA].w++; t[m.teamA].pts += 3; } if (t[m.teamB]) t[m.teamB].l++; }
      else if (b > a) { if (t[m.teamB]) { t[m.teamB].w++; t[m.teamB].pts += 3; } if (t[m.teamA]) t[m.teamA].l++; }
      else { if (t[m.teamA]) { t[m.teamA].d++; t[m.teamA].pts++; } if (t[m.teamB]) { t[m.teamB].d++; t[m.teamB].pts++; } }
    }
  });
  return Object.entries(t).sort((a, b) => b[1].pts - a[1].pts || b[1].pd - a[1].pd);
}

export function sidePlayerNames(team) {
  if (team?.slots?.length) {
    return team.slots.map((s) => s.displayName);
  }
  return (team?.players || []).map((p) =>
    typeof p === "string" ? p : p?.displayName || p?.name || ""
  );
}

export function computeStats(history, players) {
  const pS = {}, tS = {};
  const names = (players || []).map((p) => (typeof p === "string" ? p : p?.displayName || p?.name || ""));
  names.forEach((p) => { if (p) pS[p] = { wins: 0, losses: 0, matches: 0, setsWon: 0, pointsWon: 0, pointsLost: 0 }; });
  history.forEach(m => {
    const kA = m.teamA.name, kB = m.teamB.name;
    const sideAPlayers = sidePlayerNames(m.teamA);
    const sideBPlayers = sidePlayerNames(m.teamB);
    if (!tS[kA]) tS[kA] = { wins: 0, losses: 0, setsWon: 0, pointsWon: 0, pointsLost: 0, players: sideAPlayers };
    if (!tS[kB]) tS[kB] = { wins: 0, losses: 0, setsWon: 0, pointsWon: 0, pointsLost: 0, players: sideBPlayers };
    const aw = m.winner === "A";
    if (aw) { tS[kA].wins++; tS[kB].losses++; } else { tS[kB].wins++; tS[kA].losses++; }
    m.sets.forEach(s => {
      tS[kA].setsWon += s.winner === "A" ? 1 : 0; tS[kA].pointsWon += s.scoreA; tS[kA].pointsLost += s.scoreB;
      tS[kB].setsWon += s.winner === "B" ? 1 : 0; tS[kB].pointsWon += s.scoreB; tS[kB].pointsLost += s.scoreA;
    });
    [...sideAPlayers, ...sideBPlayers].forEach(p => {
      if (!pS[p]) pS[p] = { wins: 0, losses: 0, matches: 0, setsWon: 0, pointsWon: 0, pointsLost: 0 }; pS[p].matches++;
    });
    [{ pl: sideAPlayers, won: aw }, { pl: sideBPlayers, won: !aw }].forEach(({ pl, won }) =>
      pl.forEach(p => { if (!pS[p]) pS[p] = { wins: 0, losses: 0, matches: 0, setsWon: 0, pointsWon: 0, pointsLost: 0 }; if (won) pS[p].wins++; else pS[p].losses++; })
    );
  });
  return { pS, tS };
}
